import { z } from "zod";

import {
  DOMAINS,
  type Domain,
  type LevelDefinition
} from "@/domain";
import {
  DIFFICULTIES,
  LEVELS_PER_ACTIVITY,
  LEVELS_PER_DOMAIN,
  TOTAL_LEVELS,
  levelDefinitionSchema
} from "@/domain/schemas";

import {
  ACTIVITY_PAYLOAD_SCHEMAS,
  type ActivityPayloadSchemaCatalog
} from "./activity-payload-schemas";

export type DomainLevelArrays = Readonly<Record<Domain, readonly unknown[]>>;

export type RegistryValidationErrorCode =
  | "invalid-level"
  | "invalid-payload"
  | "unknown-activity"
  | "domain-mismatch"
  | "duplicate-level-id"
  | "invalid-total-count"
  | "invalid-domain-count"
  | "invalid-activity-count"
  | "invalid-difficulty-coverage";

export interface RegistryValidationError {
  readonly code: RegistryValidationErrorCode;
  readonly levelId: string;
  readonly path: string;
  readonly message: string;
}

export type RegistryValidationResult =
  | {
      readonly success: true;
      readonly levels: readonly LevelDefinition[];
      readonly errors: readonly [];
    }
  | {
      readonly success: false;
      readonly levels: readonly LevelDefinition[];
      readonly errors: readonly RegistryValidationError[];
    };

export class RegistryValidationFailure extends Error {
  readonly errors: readonly RegistryValidationError[];

  constructor(errors: readonly RegistryValidationError[]) {
    super(errors.map(formatRegistryValidationError).join("\n"));
    this.name = "RegistryValidationFailure";
    this.errors = errors;
  }
}

function pathSegment(segment: PropertyKey, index: number): string {
  if (typeof segment === "number") {
    return "[" + segment + "]";
  }

  const text = String(segment);
  return index === 0 ? text : "." + text;
}

export function formatFieldPath(path: readonly PropertyKey[]): string {
  return path.length === 0
    ? "<root>"
    : path.map(pathSegment).join("");
}

export function formatRegistryValidationError(
  error: RegistryValidationError
): string {
  return error.levelId + " " + error.path + ": " + error.message;
}

function rawLevelId(
  value: unknown,
  domain: Domain,
  index: number
): string {
  if (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof value.id === "string" &&
    value.id.length > 0
  ) {
    return value.id;
  }

  return "<" + domain + "[" + index + "]>";
}

function addZodErrors(
  errors: RegistryValidationError[],
  code: "invalid-level" | "invalid-payload",
  levelId: string,
  prefix: readonly PropertyKey[],
  zodError: z.ZodError
): void {
  for (const issue of zodError.issues) {
    errors.push({
      code,
      levelId,
      path: formatFieldPath([...prefix, ...issue.path]),
      message: issue.message
    });
  }
}

function validateCounts(
  levels: readonly LevelDefinition[],
  activitySchemas: ActivityPayloadSchemaCatalog,
  errors: RegistryValidationError[]
): void {
  if (levels.length !== TOTAL_LEVELS) {
    errors.push({
      code: "invalid-total-count",
      levelId: "<registry>",
      path: "levels",
      message:
        "Expected exactly " + TOTAL_LEVELS +
        " schema-valid levels, received " + levels.length
    });
  }

  for (const domain of DOMAINS) {
    const domainLevels = levels.filter((level) => level.domain === domain);
    if (domainLevels.length !== LEVELS_PER_DOMAIN) {
      errors.push({
        code: "invalid-domain-count",
        levelId: "<registry>",
        path: "domains." + domain,
        message:
          "Expected exactly " + LEVELS_PER_DOMAIN +
          " schema-valid levels, received " + domainLevels.length
      });
    }

    for (const activityType of Object.keys(activitySchemas[domain])) {
      const activityLevels = domainLevels.filter(
        (level) => level.activityType === activityType
      );

      if (activityLevels.length !== LEVELS_PER_ACTIVITY) {
        errors.push({
          code: "invalid-activity-count",
          levelId: "<registry>",
          path: "domains." + domain + ".activities." + activityType,
          message:
            "Expected exactly " + LEVELS_PER_ACTIVITY +
            " schema-valid levels, received " + activityLevels.length
        });
      }

      const difficulties = activityLevels.map(({ difficulty }) => difficulty);
      if (
        difficulties.length !== DIFFICULTIES.length ||
        new Set(difficulties).size !== difficulties.length ||
        !DIFFICULTIES.every((difficulty) => difficulties.includes(difficulty))
      ) {
        errors.push({
          code: "invalid-difficulty-coverage",
          levelId: "<registry>",
          path:
            "domains." + domain + ".activities." +
            activityType + ".difficulty",
          message: "Expected difficulties 1 through 6 exactly once"
        });
      }
    }
  }
}

export function validateContentRegistry(
  levelsByDomain: DomainLevelArrays,
  activitySchemas: ActivityPayloadSchemaCatalog = ACTIVITY_PAYLOAD_SCHEMAS
): RegistryValidationResult {
  const errors: RegistryValidationError[] = [];
  const levels: LevelDefinition[] = [];
  const seenIds = new Set<string>();

  for (const domain of DOMAINS) {
    const domainLevels = levelsByDomain[domain];

    domainLevels.forEach((candidate, index) => {
      const candidateId = rawLevelId(candidate, domain, index);
      const parsedLevel = levelDefinitionSchema.safeParse(candidate);

      if (!parsedLevel.success) {
        addZodErrors(
          errors,
          "invalid-level",
          candidateId,
          [],
          parsedLevel.error
        );
        return;
      }

      const level = parsedLevel.data;
      levels.push(level);

      if (level.domain !== domain) {
        errors.push({
          code: "domain-mismatch",
          levelId: level.id,
          path: "domain",
          message: "Level appears in the " + domain + " domain array"
        });
      }

      if (seenIds.has(level.id)) {
        errors.push({
          code: "duplicate-level-id",
          levelId: level.id,
          path: "id",
          message: "Level identifier is registered more than once"
        });
      }
      seenIds.add(level.id);

      const payloadSchema = activitySchemas[level.domain][level.activityType];
      if (payloadSchema === undefined) {
        errors.push({
          code: "unknown-activity",
          levelId: level.id,
          path: "activityType",
          message:
            "Unknown activity " + level.domain + "/" + level.activityType
        });
        return;
      }

      const parsedPayload = payloadSchema.safeParse(level.payload);
      if (!parsedPayload.success) {
        addZodErrors(
          errors,
          "invalid-payload",
          level.id,
          ["payload"],
          parsedPayload.error
        );
      }
    });
  }

  validateCounts(levels, activitySchemas, errors);

  return errors.length === 0
    ? { success: true, levels, errors: [] }
    : { success: false, levels, errors };
}

export function parseContentRegistry(
  levelsByDomain: DomainLevelArrays,
  activitySchemas: ActivityPayloadSchemaCatalog = ACTIVITY_PAYLOAD_SCHEMAS
): readonly LevelDefinition[] {
  const result = validateContentRegistry(levelsByDomain, activitySchemas);
  if (!result.success) {
    throw new RegistryValidationFailure(result.errors);
  }

  return result.levels;
}
